import { MENU_SCAN_MAX_IMAGE_BYTES } from '@pulsify/shared/dto'
import { isPdfFile } from './pick-files'

const MAX_EDGE = 1600
const JPEG_QUALITY = 0.85
const UNCHANGED_JPEG_BYTES = 4 * 1024 * 1024
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

export interface DecodedImage {
  width: number
  height: number
  /** The image drawn at the given size as a JPEG, or null when the browser cannot encode it. */
  toJpeg: (width: number, height: number, quality: number) => Promise<Blob | null>
  close: () => void
}

type DecodeImage = (file: File) => Promise<DecodedImage>

export type PreparedUpload =
  | { ok: true; file: File }
  | { ok: false; name: string; reason: 'undecodable' | 'too_large' }

export function fitWithin(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= maxEdge || longest === 0) return { width, height }
  const scale = maxEdge / longest
  return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

function encoder(source: CanvasImageSource): DecodedImage['toJpeg'] {
  return (width, height, quality) =>
    new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const context = canvas.getContext('2d')
      if (!context) {
        resolve(null)
        return
      }
      context.drawImage(source, 0, 0, width, height)
      canvas.toBlob(resolve, 'image/jpeg', quality)
    })
}

function loadImageElement(file: File): Promise<DecodedImage> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () =>
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
        toJpeg: encoder(image),
        close: () => URL.revokeObjectURL(url),
      })
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Undecodable image'))
    }
    image.src = url
  })
}

async function decodeInBrowser(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
      .catch(() => createImageBitmap(file))
      .catch(() => null)
    if (bitmap) {
      return {
        width: bitmap.width,
        height: bitmap.height,
        toJpeg: encoder(bitmap),
        close: () => bitmap.close(),
      }
    }
  }
  return loadImageElement(file)
}

function withinLimit(file: File, name: string): PreparedUpload {
  return file.size <= MENU_SCAN_MAX_IMAGE_BYTES
    ? { ok: true, file }
    : { ok: false, name, reason: 'too_large' }
}

/**
 * Gets a picked file ready to send: a PDF as it is, a photo shrunk to 1600 px as a JPEG.
 * A photo the browser cannot open (HEIC on Windows) comes back as `undecodable`.
 */
export async function prepareUpload(
  file: File,
  decode: DecodeImage = decodeInBrowser,
): Promise<PreparedUpload> {
  if (isPdfFile(file)) return { ok: true, file }
  let image: DecodedImage
  try {
    image = await decode(file)
  } catch {
    return { ok: false, name: file.name, reason: 'undecodable' }
  }
  try {
    const size = fitWithin(image.width, image.height, MAX_EDGE)
    const unscaled = size.width === image.width && size.height === image.height
    if (unscaled && file.type === 'image/jpeg' && file.size <= UNCHANGED_JPEG_BYTES) {
      return { ok: true, file }
    }
    const jpeg = await image.toJpeg(size.width, size.height, JPEG_QUALITY).catch(() => null)
    if (jpeg) {
      const base = file.name.replace(/\.[^.]+$/, '') || 'menu'
      const resized = new File([jpeg], `${base}.jpg`, { type: 'image/jpeg' })
      return withinLimit(resized, file.name)
    }
    return ACCEPTED_IMAGE_TYPES.has(file.type)
      ? withinLimit(file, file.name)
      : { ok: false, name: file.name, reason: 'undecodable' }
  } finally {
    image.close()
  }
}
